import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteSessionDataset,
  listSessionDatasets,
  MAX_SESSION_DATASET_FILE_BYTES,
  MAX_SESSION_DATASET_FILE_SIZE_LABEL,
  updateSessionDataset,
  uploadSessionDataset,
} from 'api/agent';
import { compact } from 'es-toolkit';
import { filter, map } from 'es-toolkit/compat';
import { SessionDataset, UpdateSessionDatasetRequest } from 'interfaces/agentSession';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';

import Loader, { LoadingStage } from 'components/Loader';
import DatasetCardSkeleton from './DatasetCardSkeleton';
import SessionDatasetCard from './SessionDatasetCard';
import {
  StudyReportDataAvailabilitySummary,
  useStudyReportDataAvailability,
} from './StudyReportDataAvailabilitySummary';
import { removeDatasetFromCache, upsertDatasetInCache } from './studyReportDatasetsUtils';

interface Props {
  sessionId: string;
  scopedStudyIds?: string[];
}

// Container component that orchestrates dataset CRUD and query-cache updates.
// Rendering details are delegated to focused child components.
const StudyReportDatasetsPanel: React.FC<Props> = ({ sessionId, scopedStudyIds = [] }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const datasetQueryKey = React.useMemo(() => ['sessionDatasets', sessionId], [sessionId]);
  const [pendingUploads, setPendingUploads] = React.useState<Array<{ id: string; fileName: string }>>([]);

  const datasetsQuery = useQuery({
    queryKey: datasetQueryKey,
    queryFn: () => listSessionDatasets(sessionId),
    enabled: Boolean(sessionId),
    placeholderData: () => queryClient.getQueryData<SessionDataset[]>(datasetQueryKey),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const uploadMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Upload report dataset',
    mutationFn: (file: File) => uploadSessionDataset(sessionId, file),
    onSuccess: (dataset) =>
      queryClient.setQueryData<SessionDataset[]>(datasetQueryKey, (currentDatasets) =>
        upsertDatasetInCache(currentDatasets, dataset)
      ),
  });

  const updateMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Update dataset metadata',
    mutationFn: ({ datasetId, request }: { datasetId: string; request: UpdateSessionDatasetRequest }) =>
      updateSessionDataset(sessionId, datasetId, request),
    onSuccess: (dataset) =>
      queryClient.setQueryData<SessionDataset[]>(datasetQueryKey, (currentDatasets) =>
        upsertDatasetInCache(currentDatasets, dataset)
      ),
  });

  const deleteMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Delete report dataset',
    mutationFn: (datasetId: string) => deleteSessionDataset(sessionId, datasetId),
    onSuccess: ({ datasetId }) =>
      queryClient.setQueryData<SessionDataset[]>(datasetQueryKey, (currentDatasets) =>
        removeDatasetFromCache(currentDatasets, datasetId)
      ),
  });

  // Upload selected files sequentially so each upload lifecycle (snackbar + cache update)
  // completes deterministically before moving to the next file.
  const handleFileSelection = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = compact(Array.from(event.target.files || []));
      const oversizedFiles = filter(files, (file) => file.size > MAX_SESSION_DATASET_FILE_BYTES);
      if (oversizedFiles.length === 1) {
        enqueueSnackbar(
          `${oversizedFiles[0].name} exceeds the ${MAX_SESSION_DATASET_FILE_SIZE_LABEL} upload limit and was skipped.`,
          {
            variant: 'error',
          }
        );
      } else if (oversizedFiles.length > 1) {
        enqueueSnackbar(
          `${oversizedFiles.length} files exceed the ${MAX_SESSION_DATASET_FILE_SIZE_LABEL} upload limit and were skipped.`,
          {
            variant: 'error',
          }
        );
      }

      const validFiles = filter(files, (file) => file.size <= MAX_SESSION_DATASET_FILE_BYTES);
      const queuedUploads = map(validFiles, (file, index) => ({
        id: `${file.name}-${file.size}-${index}`,
        file,
        fileName: file.name,
      }));

      if (!queuedUploads.length) {
        event.target.value = '';
        return;
      }

      setPendingUploads(map(queuedUploads, ({ id, fileName }) => ({ id, fileName })));

      try {
        for (const queuedUpload of queuedUploads) {
          try {
            await uploadMutation.mutateAsync(queuedUpload.file);
          } finally {
            setPendingUploads((currentUploads) =>
              filter(currentUploads, (pendingUpload) => pendingUpload.id !== queuedUpload.id)
            );
          }
        }
      } finally {
        setPendingUploads([]);
        event.target.value = '';
      }
    },
    [enqueueSnackbar, uploadMutation]
  );

  const isUploadingDatasets = pendingUploads.length > 0;
  const activeUpload = pendingUploads[0] || null;
  const queuedUploadCount = Math.max(pendingUploads.length - 1, 0);
  const datasets = datasetsQuery.data || [];
  const hasDatasets = datasets.length > 0;
  const hasResolvedDatasetList = typeof datasetsQuery.data !== 'undefined';
  const isDatasetListLoading = datasetsQuery.isLoading || (datasetsQuery.isFetching && !hasResolvedDatasetList);
  const shouldShowInitialDatasetLoader = isDatasetListLoading;
  const isClinicalAvailabilityLoading = isDatasetListLoading;
  const { availabilityItems } = useStudyReportDataAvailability({
    sessionId,
    scopedStudyIds,
    datasets,
    isDatasetAvailabilityLoading: isClinicalAvailabilityLoading,
  });

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={600}>
              Uploaded datasets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload CSV or Excel datasets up to {MAX_SESSION_DATASET_FILE_SIZE_LABEL}, describe them with editable
              metadata, and let the analysis agent use them alongside the current study cases.
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isUploadingDatasets ? <CircularProgress color="inherit" size={16} /> : <UploadFileOutlinedIcon />
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingDatasets}
          >
            {isUploadingDatasets ? 'Uploading dataset…' : 'Upload datasets'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept=".csv,.xls,.xlsx"
            onChange={handleFileSelection}
          />
        </Stack>

        <StudyReportDataAvailabilitySummary availabilityItems={availabilityItems} />

        {!datasetsQuery.isLoading && isUploadingDatasets ? (
          <Stack spacing={1.5}>
            <Paper
              variant="outlined"
              role="status"
              aria-live="polite"
              sx={{
                px: 2,
                py: 1.5,
                borderStyle: 'dashed',
                bgcolor: 'action.hover',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={18} />
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {activeUpload ? `Uploading ${activeUpload.fileName}…` : 'Uploading dataset…'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {queuedUploadCount > 0
                      ? `${queuedUploadCount} more file${queuedUploadCount === 1 ? '' : 's'} queued next.`
                      : 'Preparing the dataset preview table and editable metadata.'}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
            <DatasetCardSkeleton />
          </Stack>
        ) : null}

        {shouldShowInitialDatasetLoader ? (
          <Box sx={{ py: 1 }}>
            <Loader size="small" stage={LoadingStage.LoadingReportData} />
          </Box>
        ) : hasDatasets ? (
          <Stack spacing={2}>
            {map(datasets, (dataset) => (
              <SessionDatasetCard
                key={dataset.id}
                dataset={dataset}
                onDelete={(datasetId) => deleteMutation.mutate(datasetId)}
                onSave={(datasetId, request) => updateMutation.mutate({ datasetId, request })}
                isSaving={updateMutation.isLoading}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              No datasets have been uploaded to this report session yet.
            </Typography>
            {datasetsQuery.isFetching ? (
              <Typography variant="caption" color="text.secondary">
                Checking for uploaded datasets...
              </Typography>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default StudyReportDatasetsPanel;
